import { useState } from 'react';
import { Group, Badge, Table, Select, Center, Loader, Button, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconUsers, IconPlus, IconDownload } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { Specialty } from '../components/Specialty';
import { formatDate, formatPhone } from '../lib/format';
import { StaffCreateModal } from '../components/StaffCreateModal';
import { FaceIdCell, useFaceLinks } from '../components/FaceIdLink';

interface TeacherRow {
  id: string; login: string; fullName: string; role: string; phone?: string | null; birthDate?: string | null;
  isActive: boolean; createdAt: string; canEnterTestScores?: boolean; specialty?: string | null;
}

/** Bu sahifadan yaratiladigan rollar — ro'yxat doirasi bilan AYNI (`roles` so'rovi).
    Boshqaruv rollari "Xodimlar" sahifasida qoladi: bu yerdan tasodifan direktor
    yaratilib qolmasin. */
const TEACHER_ROLES = ['TEACHER', 'HOMEROOM_TEACHER'];

/** O'qituvchilar — alohida sahifa: qidiruv, rol filtri, saralash. */
export default function Teachers() {
  const { t } = useTranslation();
  const face = useFaceLinks();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(''); // '' | 'true' | 'false' → ?isActive=
  const [sort, setSort] = useState('fullName:asc');
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    roles: roleFilter || 'TEACHER,HOMEROOM_TEACHER',
    isActive: statusFilter || undefined,
    q: q || undefined,
    sort,
  };

  const teachers = usePagedQuery<TeacherRow>('teachers-page', '/staff', filtrlar);
  // Filtrlangan bo'sh natija ("Bloklangan" tanlansa-yu bironta ham bo'lmasa) —
  // ma'lumot umuman yo'qligidan farqli matn ko'rsatiladi.
  const filterActive = !!q || !!roleFilter || !!statusFilter;

  /**
   * CSV eksport — yuz tanish terminaliga (Hikvision) yuklash uchun ro'yxat.
   * Faylni server tayyorlaydi (`GET /staff/export`), shu sabab `responseType: 'blob'`:
   * axios'ning standart JSON o'girishi CSV matnini buzardi. Yuklab olish esa oddiy
   * havola bilan emas (endpoint Authorization header talab qiladi), balki blob'dan
   * vaqtinchalik `URL.createObjectURL` yasab, ko'rinmas <a download> bosish orqali.
   */
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get<Blob>('/staff/export', {
        // Rol filtri tanlangan bo'lsa eksport ham ro'yxat bilan bir xil doirada bo'ladi —
        // ekranda ko'rinmagan xodim faylga "yashirincha" tushib qolmasin.
        params: { role: roleFilter || 'TEACHER,HOMEROOM_TEACHER' },
        responseType: 'blob',
      });
      // Fayl nomi serverdagi Content-Disposition'dan (sana bilan). CORS sozlamasi
      // bu headerni expose qilmasligi mumkin — u holda zaxira nom ishlatiladi.
      const cd = String(res.headers['content-disposition'] ?? '');
      const name = /filename="?([^";]+)"?/i.exec(cd)?.[1] ?? 'xodimlar.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifications.show({ color: 'red', message: t('common.error') });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('teachersPage.title')}
        subtitle={`${t('common.total')}: ${teachers.total}`}
        action={(
          <Group gap="xs" wrap="wrap" justify="flex-end">
            {/* Qisqa tushuntirish tugma YONIDA turadi: eksport nima uchunligi tooltip
                ochilmasa ham ko'rinsin; tor ekranda joy tejab yashiriladi (tooltip qoladi) */}
            <Text size="xs" c="dimmed" maw={230} ta="right" visibleFrom="sm">{t('teachersPage.exportHint')}</Text>
            <Tooltip label={t('teachersPage.exportHint')} withArrow>
              <Button variant="light" leftSection={<IconDownload size={18} />} loading={exporting} onClick={handleExport}>
                {t('teachersPage.exportCsv')}
              </Button>
            </Tooltip>
            <Button leftSection={<IconPlus size={18} />} onClick={openAdd}>{t('teachersPage.add')}</Button>
          </Group>
        )}
      />
      <StaffCreateModal
        opened={addOpened}
        onClose={closeAdd}
        roles={TEACHER_ROLES}
        title={t('teachersPage.add')}
      />
      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); teachers.setPage(1); }}
        searchPlaceholder={t('teachersPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); teachers.setPage(1); }}
        sortOptions={[
          { value: 'fullName:asc', label: t('teachersPage.sortNameAsc') },
          { value: 'fullName:desc', label: t('teachersPage.sortNameDesc') },
          { value: 'createdAt:desc', label: t('teachersPage.sortNewest') },
          { value: 'role:asc', label: t('teachersPage.sortRole') },
        ]}
      >
        <Select
          placeholder={t('teachersPage.role')}
          data={[
            { value: '', label: t('teachersPage.allRoles') },
            { value: 'TEACHER', label: t('roles.TEACHER') },
            { value: 'HOMEROOM_TEACHER', label: t('roles.HOMEROOM_TEACHER') },
          ]}
          value={roleFilter}
          onChange={(v) => { setRoleFilter(v ?? ''); teachers.setPage(1); }}
          w={180}
          clearable
        />
        <Select
          placeholder={t('teacherDetail.status')}
          data={[
            { value: 'true', label: t('teachersPage.active') },
            { value: 'false', label: t('block.blocked') },
          ]}
          value={statusFilter || null}
          onChange={(v) => { setStatusFilter(v ?? ''); teachers.setPage(1); }}
          w={160}
          clearable
        />
        <TableExport<TeacherRow>
          endpoint="/staff"
          params={filtrlar}
          filename={t('nav.teachers')}
          ustunlar={[
            { key: 'fullName', label: t('teacherDetail.fullName') },
            { key: 'login', label: t('teachersPage.login') },
            { key: 'role', label: t('teachersPage.role'), get: (r) => t(`roles.${r.role}`) },
            { key: 'specialty', label: t('staffPage.specialty') },
            { key: 'birthDate', label: t('teacherDetail.birthDate'), get: (r) => (r.birthDate ? formatDate(r.birthDate) : '') },
            { key: 'phone', label: t('teacherDetail.phone') },
            { key: 'createdAt', label: t('teacherDetail.joined') },
            { key: 'isActive', label: t('teacherDetail.status'), get: (r) => (r.isActive ? '+' : '-') },
          ]}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconUsers} p={0} pos="relative">
        <FetchOverlay visible={teachers.fetching} />
        {teachers.isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !teachers.items.length ? (
          <EmptyState icon={IconUsers} title={filterActive ? t('common.noMatch') : t('common.noData')} />
        ) : (
        <Table.ScrollContainer minWidth={face.enabled ? 1080 : 920}>
          <Table className="jadval-toliq" highlightOnHover verticalSpacing="sm" style={{ whiteSpace: 'nowrap' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('teacherDetail.fullName')}</Table.Th>
                <Table.Th>{t('teachersPage.login')}</Table.Th>
                <Table.Th>{t('teachersPage.role')}</Table.Th>
                <Table.Th>{t('teacherDetail.birthDate')}</Table.Th>
                <Table.Th>{t('teacherDetail.phone')}</Table.Th>
                <Table.Th>{t('teacherDetail.joined')}</Table.Th>
                <Table.Th>{t('teacherDetail.status')}</Table.Th>
                {face.enabled && <Table.Th>FACE ID</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teachers.items.map((u) => (
                <Table.Tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/teachers/${u.id}`)}>
                  <Table.Td fw={500}>{u.fullName}<Specialty value={u.specialty} /></Table.Td>
                  <Table.Td ff="monospace">{u.login}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Badge variant="light" color={u.role === 'HOMEROOM_TEACHER' ? 'grape' : 'blue'}>{t(`roles.${u.role}`)}</Badge>
                      {u.canEnterTestScores && <Badge color="grape" variant="light">{t('examsPage.testTeacher')}</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td>{formatDate(u.birthDate)}</Table.Td>
                  <Table.Td>{formatPhone(u.phone)}</Table.Td>
                  <Table.Td>{formatDate(u.createdAt)}</Table.Td>
                  <Table.Td><Badge variant="light" color={u.isActive ? 'teal' : 'gray'}>{u.isActive ? t('teachersPage.active') : t('block.blocked')}</Badge></Table.Td>
                  {face.enabled && <Table.Td><FaceIdCell user={u} link={face.map[u.id]} /></Table.Td>}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={teachers.page}
        pages={teachers.pages}
        total={teachers.total}
        limit={teachers.limit}
        onPage={teachers.setPage}
        onLimit={teachers.setLimit}
      />
    </>
  );
}
